import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import Icon from '@mui/material/Icon';
import Swal from 'sweetalert2';
import { api } from 'api';

// Cell components for DataTable
function PermissionsCell({ value }) {
  const permissionCount = value?.length || 0;
  return (
    <MDTypography variant="caption" color="text">
      {permissionCount} permission(s)
    </MDTypography>
  );
}

PermissionsCell.propTypes = {
  value: PropTypes.arrayOf(PropTypes.object),
};

function ActionsCell({ row, onEdit, onDelete }) {
  const roleData = row.original;
  const isProtectedRole = roleData.name === 'Admin' || roleData.name === 'Super Admin' || roleData.name === 'Administrator';
  
  return (
    <MDBox display="flex" gap={1} justifyContent="center">
      <IconButton
        size="small"
        color="info"
        onClick={() => onEdit(roleData)}
      >
        <Icon>edit</Icon>
      </IconButton>
      <IconButton
        size="small"
        color="error"
        onClick={() => onDelete(roleData.id)}
        disabled={isProtectedRole}
        sx={{
          opacity: isProtectedRole ? 0.5 : 1,
          cursor: isProtectedRole ? 'not-allowed' : 'pointer',
        }}
      >
        <Icon>delete</Icon>
      </IconButton>
    </MDBox>
  );
}

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function RoleManagement() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    permission_ids: [],
  });

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await api.role.getAllRoles();
      if (response?.data) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load roles',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await api.role.getAllPermissions();
      if (response?.data) {
        setPermissions(response.data);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditMode(true);
      setCurrentRole(role);
      setFormData({
        name: role.name,
        slug: role.slug,
        permission_ids: role.permissions?.map((p) => p.id) || [],
      });
    } else {
      setEditMode(false);
      setCurrentRole(null);
      setFormData({
        name: '',
        slug: '',
        permission_ids: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentRole(null);
    setFormData({
      name: '',
      slug: '',
      permission_ids: [],
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Auto-generate slug from name
    if (name === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData((prev) => ({
        ...prev,
        slug: slug,
      }));
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData((prev) => {
      const isSelected = prev.permission_ids.includes(permissionId);
      return {
        ...prev,
        permission_ids: isSelected
          ? prev.permission_ids.filter((id) => id !== permissionId)
          : [...prev.permission_ids, permissionId],
      };
    });
  };

  const handleSelectAll = () => {
    if (formData.permission_ids.length === permissions.length) {
      // Deselect all
      setFormData((prev) => ({
        ...prev,
        permission_ids: [],
      }));
    } else {
      // Select all
      setFormData((prev) => ({
        ...prev,
        permission_ids: permissions.map((p) => p.id),
      }));
    }
  };

  // Group permissions by module
  const groupPermissionsByModule = () => {
    const groups = {
      'Auth': [],
      'Contact': [],
      'OPD History': [],
      'OPD Prescription': [],
      'Role Management': [],
      'Other': [],
    };

    permissions.forEach((permission) => {
      const slug = permission.slug.toLowerCase();
      if (slug.includes('dashboard')) {
        groups['Auth'].push(permission);
      } else if (slug.includes('contact') || slug.includes('patient') || slug.includes('user')) {
        groups['Contact'].push(permission);
      } else if (slug.includes('opd-history') || slug.includes('obsteric') || slug.includes('miscarriage') || slug.includes('menstrual')) {
        groups['OPD History'].push(permission);
      } else if (slug.includes('opd-prescription') || slug.includes('prescription')) {
        groups['OPD Prescription'].push(permission);
      } else if (slug.includes('role') || slug.includes('permission') || slug.includes('usertype')) {
        groups['Role Management'].push(permission);
      } else {
        groups['Other'].push(permission);
      }
    });

    return groups;
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Name and Slug are required',
      });
      return;
    }

    try {
      setLoading(true);
      if (editMode) {
        await api.role.updateRole(currentRole.id, formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Role updated successfully',
        });
      } else {
        await api.role.createRole(formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Role created successfully',
        });
      }
      handleCloseDialog();
      loadRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save role',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete the role permanently',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await api.role.deleteRole(roleId);
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Role deleted successfully',
        });
        loadRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete role',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      align: 'left',
      headerStyle: {
        color: '#018EA1',
        fontWeight: 'bold',
      },
    },
    {
      Header: 'Slug',
      accessor: 'slug',
      align: 'left',
      headerStyle: {
        color: '#018EA1',
        fontWeight: 'bold',
      },
    },
    {
      Header: 'Permissions',
      accessor: 'permissions',
      align: 'left',
      Cell: PermissionsCell,
      headerStyle: {
        color: '#018EA1',
        fontWeight: 'bold',
      },
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      align: 'center',
      Cell: (props) => (
        <ActionsCell
          {...props}
          onEdit={handleOpenDialog}
          onDelete={handleDelete}
        />
      ),
      headerStyle: {
        color: '#018EA1',
        fontWeight: 'bold',
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Role Management
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="white"
                  onClick={() => handleOpenDialog()}
                >
                  <Icon sx={{ mr: 1 }}>add</Icon>
                  Create Role
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows: roles }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Slug"
              name="slug"
              value={formData.slug}
              margin="normal"
              required
              disabled
              helperText="Auto-generated from name"
              InputProps={{
                readOnly: true,
              }}
            />
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Permissions
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2, 
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" fontWeight="medium" color="text.primary">
                {formData.permission_ids.length} of {permissions.length} permissions selected
              </Typography>
              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={handleSelectAll}
              >
                <Icon sx={{ mr: 1 }}>{formData.permission_ids.length === permissions.length ? 'check_box_outline_blank' : 'check_box'}</Icon>
                {formData.permission_ids.length === permissions.length ? 'Deselect All' : 'Select All'}
              </MDButton>
            </Box>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, backgroundColor: '#fafafa' }}>
              {Object.entries(groupPermissionsByModule()).map(([moduleName, modulePermissions]) => (
                modulePermissions.length > 0 && (
                  <Box key={moduleName} sx={{ mb: 3, backgroundColor: 'white', borderRadius: '8px', p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#018EA1',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid #018EA1',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Icon sx={{ mr: 1 }}>folder</Icon>
                      {moduleName}
                    </Typography>
                    <Grid container spacing={1}>
                      {modulePermissions.map((permission) => (
                        <Grid item xs={12} sm={6} key={permission.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.permission_ids.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                sx={{
                                  color: '#018EA1',
                                  '&.Mui-checked': {
                                    color: '#018EA1',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ ml: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                  {permission.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                                  {permission.slug}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              width: '100%',
                              m: 0,
                              p: 1.5,
                              borderRadius: '6px',
                              border: '1px solid',
                              borderColor: formData.permission_ids.includes(permission.id) ? '#018EA1' : 'transparent',
                              backgroundColor: formData.permission_ids.includes(permission.id) ? 'rgba(1, 142, 161, 0.08)' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: formData.permission_ids.includes(permission.id) ? 'rgba(1, 142, 161, 0.12)' : 'rgba(1, 142, 161, 0.04)',
                                borderColor: '#018EA1',
                              },
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <MDButton onClick={handleCloseDialog} variant="outlined" color="dark">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            variant="gradient"
            color="dark"
            disabled={loading}
          >
            {editMode ? 'Update' : 'Create'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

RoleManagement.propTypes = {};

export default RoleManagement;
